import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import {
  User, Calendar, MessageSquare, Heart, Settings, LogOut,
  Search, Clock, Star, ChevronRight, Bell, Target
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { useFavorites } from "../../context/FavoritesContext";
import { useBooking } from "../../context/BookingContext";
import { expertsData } from "../../data/experts";
import ConsultationCard from "../../components/ui/ConsultationCard";
import ProgressBar from "../../components/ui/ProgressBar";
import ClientOnboardingWizard from "../../components/ui/ClientOnboardingWizard";

export default function ClientDashboard() {
  const { user, logout, updateUser } = useAuth();
  const { favorites } = useFavorites();
  const { getBookingsForClient } = useBooking();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [clientOnboardingDone, setClientOnboardingDone] = useState(false);

  const showClientOnboarding = user?.role === "client" && !user?.client_onboarding_completed && !clientOnboardingDone;

  const handleClientOnboardingSave = async (updates) => {
    if (updateUser) {
      await updateUser(updates);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const favoriteExperts = expertsData.filter((e) => favorites.includes(e.id));

  const clientId = user?.id || "client-1";
  const clientBookings = getBookingsForClient(clientId);

  const completedBookings = clientBookings.filter((b) => b.status === "completed");
  const upcomingBookings = clientBookings.filter((b) => b.status !== "completed" && b.status !== "cancelled");

  const uniqueExperts = [...new Map(
    clientBookings.map((b) => [b.expertId, { id: b.expertId, name: b.expertName || "Эксперт" }])
  ).values()];

  const tabs = [
    { id: "overview", label: "Обзор", icon: User },
    { id: "consultations", label: "Консультации", icon: Calendar },
    { id: "progress", label: "Прогресс", icon: Target },
    { id: "favorites", label: "Избранное", icon: Heart },
    { id: "settings", label: "Настройки", icon: Settings }
  ];

  return (
    <Layout hideFooter>
      <div id="client-dashboard_page" className="min-h-screen bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-64 flex-shrink-0">
              <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800 mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                    <span className="text-white text-xl font-medium">
                      {user?.name?.[0] || "U"}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-white">
                      {user?.name || "Пользователь"}
                    </h2>
                    <p className="text-lg font-extralight text-neutral-400">
                      {user?.role === "admin" ? "Администратор" : user?.role === "expert" ? "Эксперт" : "Клиент"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-lg font-extralight text-neutral-400">
                  <Bell className="w-4 h-4" />
                  <span>2 уведомления</span>
                </div>
              </div>

              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                      activeTab === tab.id
                        ? "bg-primary-500/10 text-primary-400"
                        : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="text-lg font-extralight">{tab.label}</span>
                  </button>
                ))}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-lg font-extralight">Выйти</span>
                </button>
              </nav>
            </aside>

            <main className="flex-1">
              {showClientOnboarding ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-4"
                >
                  <ClientOnboardingWizard
                    user={user}
                    onComplete={() => setClientOnboardingDone(true)}
                    onSave={handleClientOnboardingSave}
                  />
                </motion.div>
              ) : activeTab === "overview" ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-5 h-5 text-primary-400" />
                        <span className="text-lg font-extralight text-neutral-400">Консультаций</span>
                      </div>
                      <p className="text-3xl font-medium text-white">{clientBookings.length}</p>
                    </div>
                    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
                      <div className="flex items-center gap-3 mb-2">
                        <Heart className="w-5 h-5 text-red-400" />
                        <span className="text-lg font-extralight text-neutral-400">В избранном</span>
                      </div>
                      <p className="text-3xl font-medium text-white">{favorites.length}</p>
                    </div>
                    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-5 h-5 text-yellow-400" />
                        <span className="text-lg font-extralight text-neutral-400">Запланировано</span>
                      </div>
                      <p className="text-3xl font-medium text-white">{upcomingBookings.length}</p>
                    </div>
                  </div>

                  {upcomingBookings.length > 0 && (
                    <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
                      <h3 className="text-xl font-medium text-white mb-4">Ближайшие консультации</h3>
                      <div className="space-y-4">
                        {upcomingBookings.map((b) => (
                          <div key={b.id} className="flex items-center gap-4 p-4 rounded-xl bg-neutral-800/50">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                              <span className="text-white font-medium">{b.expertName?.[0] || "E"}</span>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-lg font-medium text-white">{b.expertName || "Эксперт"}</h4>
                              <p className="text-lg font-extralight text-neutral-400">{b.topic || "Без темы"}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-medium text-white">
                                {new Date(b.date).toLocaleDateString("ru-RU")}
                              </p>
                              <p className="text-lg font-extralight text-primary-400">{b.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-500/10 to-accent-500/10 border border-primary-500/20">
                    <h3 className="text-xl font-medium text-white mb-2">Найдите эксперта</h3>
                    <p className="text-lg font-extralight text-neutral-300 mb-4">
                      Получите обратную связь от практиков с реальным опытом
                    </p>
                    <Link
                      to="/experts"
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                    >
                      <Search className="w-4 h-4" />
                      Найти эксперта
                    </Link>
                  </div>
                </motion.div>
              ) : null}

              {!showClientOnboarding && activeTab === "consultations" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-medium text-white">История консультаций</h2>

                  {clientBookings.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                      <p className="text-lg font-extralight text-neutral-400">У вас пока нет консультаций</p>
                      <Link
                        to="/experts"
                        className="inline-flex items-center gap-2 mt-4 text-primary-400 hover:text-primary-300"
                      >
                        Найти эксперта
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[...upcomingBookings, ...completedBookings].map((b) => (
                        <ConsultationCard key={b.id} booking={b} isClient={true} />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {!showClientOnboarding && activeTab === "progress" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-medium text-white">Мой прогресс</h2>

                  {uniqueExperts.length === 0 ? (
                    <div className="text-center py-12">
                      <Target className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                      <p className="text-lg font-extralight text-neutral-400">
                        Прогресс появится после первой консультации
                      </p>
                      <Link
                        to="/experts"
                        className="inline-flex items-center gap-2 mt-4 text-primary-400 hover:text-primary-300"
                      >
                        Найти эксперта
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {uniqueExperts.map((expert) => (
                        <ProgressBar
                          key={expert.id}
                          expertId={expert.id}
                          clientId={clientId}
                          expertName={expert.name}
                          isExpertView={false}
                        />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {!showClientOnboarding && activeTab === "favorites" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-medium text-white">Избранные эксперты</h2>

                  {favoriteExperts.length === 0 ? (
                    <div className="text-center py-12">
                      <Heart className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                      <p className="text-lg font-extralight text-neutral-400">У вас пока нет избранных экспертов</p>
                      <Link
                        to="/experts"
                        className="inline-flex items-center gap-2 mt-4 text-primary-400 hover:text-primary-300"
                      >
                        Найти эксперта
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {favoriteExperts.map((expert) => (
                        <Link
                          key={expert.id}
                          to={`/experts/${expert.id}`}
                          className="flex items-center gap-4 p-5 rounded-xl bg-neutral-900 border border-neutral-800 hover:border-neutral-700 transition-colors"
                        >
                          <img
                            src={expert.avatar}
                            alt={expert.name}
                            className="w-14 h-14 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <h4 className="text-lg font-medium text-white">{expert.name}</h4>
                            <p className="text-lg font-extralight text-neutral-400">{expert.title}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span className="text-lg font-medium text-white">{expert.rating}</span>
                          </div>
                          <ChevronRight className="w-5 h-5 text-neutral-500" />
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {!showClientOnboarding && activeTab === "settings" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-medium text-white">Настройки</h2>

                  <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
                    <h3 className="text-xl font-medium text-white mb-4">Личные данные</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-lg font-extralight text-neutral-400 mb-2">Имя</label>
                        <input
                          type="text"
                          defaultValue={user?.name}
                          className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-lg font-extralight text-neutral-400 mb-2">Email</label>
                        <input
                          type="email"
                          defaultValue={user?.email}
                          className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <button className="px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors">
                        Сохранить изменения
                      </button>
                    </div>
                  </div>

                  <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
                    <h3 className="text-xl font-medium text-white mb-4">Уведомления</h3>
                    <div className="space-y-4">
                      {["Email-уведомления", "Push-уведомления"].map((label) => (
                        <label key={label} className="flex items-center justify-between cursor-pointer">
                          <span className="text-lg font-extralight text-neutral-300">{label}</span>
                          <div className="relative">
                            <input type="checkbox" className="sr-only peer" defaultChecked={label.includes("Email")} />
                            <div className="w-11 h-6 bg-neutral-700 rounded-full peer peer-checked:bg-primary-600 transition-colors"></div>
                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform"></div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}
